import { useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import * as Icons from 'lucide-react';
import { canAccessRoute } from '@logisticslash/shared';
import { cn } from '@/lib/utils';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useTheme } from '@/components/ThemeProvider';
import { useAuth } from '@/components/AuthProvider';
import { Button } from '@/components/ui/button';
import { NotificationBell } from '@/components/NotificationBell';
import { api } from '@/lib/api';
import { SHELL } from '@/constants/testIds';

const NAV_GROUPS = [
  {
    title: 'Operations',
    items: [
      { href: '/', label: 'Overview', icon: 'LayoutDashboard' },
      { href: '/loads', label: 'Loads', icon: 'Package' },
      { href: '/dispatch', label: 'Dispatch', icon: 'Columns3' },
      { href: '/trips', label: 'Trips', icon: 'Route' },
      { href: '/tracking', label: 'Tracking', icon: 'MapPinned' },
    ],
  },
  {
    title: 'Network',
    items: [
      { href: '/parties', label: 'Parties', icon: 'Users' },
      { href: '/fleet', label: 'Fleet', icon: 'Truck' },
      { href: '/drivers', label: 'Drivers', icon: 'UserRound' },
    ],
  },
  {
    title: 'Finance',
    items: [
      { href: '/settlements', label: 'Settlements', icon: 'Wallet', badgeKey: 'review' },
      { href: '/pay-runs', label: 'Pay runs', icon: 'Banknote' },
      { href: '/invoices', label: 'Invoices', icon: 'FileText' },
      { href: '/ar', label: 'AR / Aging', icon: 'Landmark' },
      { href: '/collections', label: 'Collections', icon: 'Megaphone' },
      { href: '/factoring', label: 'Factoring', icon: 'Building2' },
      { href: '/ap', label: 'AP / Bills', icon: 'Receipt' },
      { href: '/ifta', label: 'IFTA', icon: 'Fuel' },
      { href: '/calculator', label: 'Calculator', icon: 'Calculator' },
    ],
  },
  {
    title: 'Tools',
    items: [
      { href: '/import', label: 'Import', icon: 'Upload' },
      { href: '/ai', label: 'Rate Con Import', icon: 'Sparkles' },
      { href: '/qb', label: 'QuickBooks', icon: 'BookOpen' },
      { href: '/docs', label: 'Documents', icon: 'FolderOpen' },
      { href: '/guides', label: 'Guides', icon: 'Library' },
      { href: '/settings', label: 'Settings', icon: 'Settings' },
    ],
  },
];

const SUPER_NAV_GROUPS = [
  {
    title: 'Platform',
    items: [
      { href: '/super', label: 'Overview', icon: 'LayoutDashboard' },
      { href: '/super/companies', label: 'Companies', icon: 'Building2' },
      { href: '/super/billing', label: 'Billing', icon: 'CreditCard' },
      { href: '/super/marketing', label: 'Marketing', icon: 'Megaphone' },
      { href: '/super/apps', label: 'Apps', icon: 'AppWindow' },
      { href: '/super/audit', label: 'Audit logs', icon: 'ScrollText' },
      { href: '/super/users', label: 'User activity', icon: 'Users' },
      { href: '/super/errors', label: 'Error tracer', icon: 'Bug' },
      { href: '/super/metrics', label: 'Request metrics', icon: 'BarChart3' },
      { href: '/super/security', label: 'Security', icon: 'Shield' },
    ],
  },
];

const PAGE_TITLES = {
  '/': 'Overview',
  '/loads': 'Loads',
  '/dispatch': 'Dispatch',
  '/trips': 'Trips',
  '/tracking': 'Tracking',
  '/parties': 'Parties',
  '/fleet': 'Fleet',
  '/drivers': 'Drivers',
  '/settlements': 'Settlements',
  '/pay-runs': 'Pay Runs',
  '/invoices': 'Invoices',
  '/ar': 'AR / Aging',
  '/collections': 'Collections',
  '/factoring': 'Factoring',
  '/ap': 'AP / Bills',
  '/ifta': 'IFTA',
  '/calculator': 'Calculator',
  '/import': 'Import',
  '/ai': 'Rate Con Import',
  '/qb': 'QuickBooks',
  '/docs': 'Documents',
  '/guides': 'Guides',
  '/settings': 'Settings',
  '/super': 'Platform',
  '/super/companies': 'Companies',
  '/super/billing': 'Billing',
  '/super/marketing': 'Marketing',
  '/super/apps': 'Apps',
  '/super/audit': 'Audit logs',
  '/super/users': 'User activity',
  '/super/errors': 'Error tracer',
  '/super/metrics': 'Request metrics',
  '/super/security': 'Security',
};

export function AppShell({ children }) {
  const [collapsed, setCollapsed] = useLocalStorage('ls-rail-collapsed', false);
  const { theme, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const role = user?.role;

  const reviewQ = useQuery({
    queryKey: ['settlements'],
    enabled: Boolean(role) && role !== 'super_admin',
    staleTime: 60_000,
    queryFn: async () => {
      const r = await api('/api/v1/settlements');
      if (r.status !== 200) return [];
      return r.data?.settlements ?? [];
    },
    select: (list) => list.filter((s) => s.needsReview && !s.reviewedAt).length,
  });

  const groups = useMemo(() => {
    const source = role === 'super_admin' ? SUPER_NAV_GROUPS : NAV_GROUPS;
    return source
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => (role ? canAccessRoute(role, item.href) : false)),
      }))
      .filter((g) => g.items.length > 0);
  }, [role]);

  const isActive = (href) => {
    if (href === '/' || href === '/super') return location.pathname === href;
    return location.pathname.startsWith(href);
  };

  const openCommand = () => window.dispatchEvent(new Event('ls:open-command'));

  const onLogout = async () => {
    await logout();
    navigate('/login');
  };

  const title =
    PAGE_TITLES[location.pathname] ||
    (location.pathname.startsWith('/super') ? 'Platform' : 'LogisticSlash');

  return (
    <div className="flex h-screen overflow-hidden">
      <aside
        data-testid={SHELL.sidebar}
        className={cn(
          'flex flex-col border-r bg-card transition-all duration-300',
          collapsed ? 'w-[72px]' : 'w-[240px]'
        )}
      >
        <div className="flex h-16 items-center gap-2 border-b px-4">
          {!collapsed && (
            <h1 className="brand-text text-xl">
              <span className="text-foreground">Logistic</span>
              <span className="text-primary">Slash</span>
            </h1>
          )}
          {collapsed && <span className="brand-text text-xl text-primary">LS</span>}
        </div>

        <nav className="flex-1 overflow-y-auto p-3 space-y-5 custom-scrollbar">
          {groups.map((group) => (
            <div key={group.title}>
              {!collapsed && (
                <p className="px-3 mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  {group.title}
                </p>
              )}
              <div className="space-y-0.5">
                {group.items.map((item) => {
                  const Icon = Icons[item.icon] || Icons.Circle;
                  const active = isActive(item.href);
                  const badge =
                    item.badgeKey === 'review' ? reviewQ.data || 0 : item.badge || 0;
                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      title={collapsed ? item.label : undefined}
                      onClick={(e) => {
                        // Keep sidebar scroll position when focusing the active link
                        e.currentTarget.focus({ preventScroll: true });
                      }}
                      className={cn(
                        'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors relative',
                        active
                          ? 'text-primary bg-primary/10'
                          : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                      )}
                    >
                      <Icon className="h-4 w-4 flex-shrink-0" />
                      {!collapsed && <span className="flex-1">{item.label}</span>}
                      {!collapsed && badge > 0 && (
                        <span className="ml-auto text-[10px] font-bold bg-destructive text-white rounded-full h-4 min-w-[16px] px-1 flex items-center justify-center">
                          {badge}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <div className="border-t p-3 space-y-1">
          {!collapsed && user?.displayName ? (
            <p className="px-3 py-1 text-xs text-muted-foreground truncate">{user.displayName}</p>
          ) : null}
          <Button
            data-testid={SHELL.themeToggle}
            variant="ghost"
            size={collapsed ? 'icon' : 'default'}
            onClick={toggleTheme}
            className={cn('w-full', !collapsed && 'justify-start')}
          >
            {theme === 'light' ? <Icons.Moon className="h-4 w-4" /> : <Icons.Sun className="h-4 w-4" />}
            {!collapsed && <span className="ml-3">Toggle theme</span>}
          </Button>
          <Button
            data-testid={SHELL.sidebarToggle}
            variant="ghost"
            size={collapsed ? 'icon' : 'default'}
            onClick={() => setCollapsed(!collapsed)}
            className={cn('w-full', !collapsed && 'justify-start')}
          >
            {collapsed ? <Icons.PanelLeftOpen className="h-4 w-4" /> : <Icons.PanelLeftClose className="h-4 w-4" />}
            {!collapsed && <span className="ml-3">Collapse</span>}
          </Button>
          <Button
            variant="ghost"
            size={collapsed ? 'icon' : 'default'}
            onClick={onLogout}
            className={cn('w-full', !collapsed && 'justify-start')}
          >
            <Icons.LogOut className="h-4 w-4" />
            {!collapsed && <span className="ml-3">Log out</span>}
          </Button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col overflow-hidden">
        <header
          data-testid={SHELL.topbar}
          className="h-16 border-b bg-card flex items-center justify-between px-6"
        >
          <div className="flex items-center gap-4">
            <h2 className="text-lg font-semibold">{title}</h2>
            <div className="flex items-center gap-2 text-xs">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
              </span>
              <span className="text-muted-foreground">
                {role === 'super_admin' ? 'Platform console' : 'Ops Workspace'}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={openCommand}
              data-testid={SHELL.commandBar}
              className="hidden md:flex items-center gap-2 px-3 h-9 rounded-md border bg-muted/50 hover:bg-muted text-sm text-muted-foreground transition-colors"
            >
              <Icons.Search className="h-4 w-4" />
              <span>Search</span>
              <kbd className="ml-4 text-[10px] bg-background px-1.5 py-0.5 rounded border">⌘K</kbd>
            </button>
            {role !== 'super_admin' ? <NotificationBell /> : null}
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6 custom-scrollbar">
          <div className="mx-auto max-w-[1440px]">{children}</div>
        </main>
      </div>
    </div>
  );
}
